import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  useTheme,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  MoreVert as MoreVertIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
} from 'recharts';
import { motion } from 'framer-motion';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';
import LocationSelector from '../components/LocationSelector';
import BrandSelector from '../components/BrandSelector';
import StoreSelector from '../components/StoreSelector';
import { format, subDays } from 'date-fns';
import { useLaborCosts } from '../hooks/useLaborData';
import { useEmployeePerformance } from '../hooks/useAnalytics';
import { formatCurrency, formatNumber } from '../utils/formatters';

interface Employee {
  id: string;
  name: string;
  role: string;
  avatar?: string;
  efficiency: number;
  hoursWorked: number;
  salesPerHour: number;
  customerRating: number;
  status: 'active' | 'break' | 'off';
}

interface LaborMetrics {
  totalStaff: number;
  laborCost: number;
  laborCostPercentage: number;
  overtimeHours: number;
  avgProductivity: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const LaborAnalytics: React.FC = () => {
  const theme = useTheme();
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Fetch real data
  const startDate = format(subDays(new Date(), 7), 'yyyy-MM-dd');
  const endDate = format(new Date(), 'yyyy-MM-dd');
  const { data: laborData } = useLaborCosts(startDate, endDate, selectedStoreId || undefined);
  const { data: employeeData } = useEmployeePerformance(undefined, selectedStoreId || undefined);

  // Combine real and mock data
  const laborMetrics: LaborMetrics = {
    totalStaff: employeeData?.employees?.length || 45,
    laborCost: laborData?.totalLaborCost || 12450,
    laborCostPercentage: laborData?.laborCostPercentage || 28.5,
    overtimeHours: 24, // Still mock
    avgProductivity: laborData?.salesPerLaborHour ? Math.min(100, (laborData.salesPerLaborHour / 100) * 100) : 87,
  };

  // Use real employee data if available, otherwise use mock
  const employees: Employee[] = employeeData?.employees ? 
    employeeData.employees.slice(0, 5).map((emp: any, index: number) => ({
      id: emp.employeeId || `${index}`,
      name: emp.employeeName || emp.name || `Employee ${emp.employeeId}`,
      role: index === 0 ? 'Shift Manager' : index < 3 ? 'Cook' : 'Cashier',
      efficiency: Math.min(100, 70 + Math.random() * 30),
      hoursWorked: 5 + Math.random() * 4,
      salesPerHour: (emp.totalSales || 0) / 8,
      customerRating: 4 + Math.random() * 0.9,
      status: index < 3 ? 'active' : index === 3 ? 'break' : 'active' as any,
    })) : [
    { id: '1', name: 'Sarah Johnson', role: 'Shift Manager', efficiency: 95, hoursWorked: 8.5, salesPerHour: 125, customerRating: 4.8, status: 'active' },
    { id: '2', name: 'Mike Chen', role: 'Cook', efficiency: 92, hoursWorked: 7.2, salesPerHour: 98, customerRating: 4.7, status: 'active' },
    { id: '3', name: 'Emily Davis', role: 'Cashier', efficiency: 88, hoursWorked: 6.5, salesPerHour: 110, customerRating: 4.9, status: 'break' },
    { id: '4', name: 'James Wilson', role: 'Cook', efficiency: 90, hoursWorked: 8.0, salesPerHour: 102, customerRating: 4.6, status: 'active' },
    { id: '5', name: 'Lisa Garcia', role: 'Server', efficiency: 85, hoursWorked: 5.5, salesPerHour: 95, customerRating: 4.8, status: 'active' },
  ];

  const weeklySchedule = [
    { day: 'Mon', scheduled: 42, actual: 40, variance: -2 },
    { day: 'Tue', scheduled: 38, actual: 41, variance: 3 },
    { day: 'Wed', scheduled: 40, actual: 40, variance: 0 },
    { day: 'Thu', scheduled: 45, actual: 48, variance: 3 },
    { day: 'Fri', scheduled: 52, actual: 55, variance: 3 },
    { day: 'Sat', scheduled: 58, actual: 56, variance: -2 },
    { day: 'Sun', scheduled: 48, actual: 50, variance: 2 },
  ];

  const laborByRole = [
    { role: 'Managers', hours: 120, cost: 3600, color: '#af2326' },
    { role: 'Cooks', hours: 280, cost: 5600, color: '#ff6b35' },
    { role: 'Cashiers', hours: 220, cost: 3300, color: '#1976d2' },
    { role: 'Servers', hours: 180, cost: 2520, color: '#4caf50' },
    { role: 'Support', hours: 100, cost: 1200, color: '#9c27b0' },
  ];

  const performanceMetrics = [
    { metric: 'Speed', 'Current Week': 85, 'Last Week': 82 },
    { metric: 'Accuracy', 'Current Week': 92, 'Last Week': 90 },
    { metric: 'Customer Service', 'Current Week': 88, 'Last Week': 85 },
    { metric: 'Efficiency', 'Current Week': 90, 'Last Week': 87 },
    { metric: 'Teamwork', 'Current Week': 95, 'Last Week': 93 },
  ];

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: Employee['status']) => {
    switch (status) {
      case 'active': return theme.palette.success.main;
      case 'break': return theme.palette.warning.main;
      case 'off': return theme.palette.text.secondary;
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4.5) return theme.palette.success.main;
    if (rating >= 4.0) return theme.palette.info.main;
    return theme.palette.warning.main;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Labor Analytics
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Staff performance, scheduling, and labor cost management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ minWidth: 250 }}>
              <BrandSelector value={selectedBrand} onChange={(brand) => setSelectedBrand(brand as string)} />
            </Box>
            <Box sx={{ minWidth: 300 }}>
              <LocationSelector value={selectedLocation} onChange={setSelectedLocation} selectedBrand={selectedBrand} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={2.4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.1 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(175, 35, 38, 0.2)' }}>
                  <PeopleIcon sx={{ color: theme.palette.primary.main }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Staff
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {laborMetrics.totalStaff}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    +3 from last week
                  </Typography>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={2.4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.2 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(255, 107, 53, 0.2)' }}>
                  <MoneyIcon sx={{ color: '#ff6b35' }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Labor Cost
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    ${laborMetrics.laborCost.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="error.main">
                    {laborMetrics.laborCostPercentage}% of sales
                  </Typography>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={2.4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.3 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(25, 118, 210, 0.2)' }}>
                  <TimeIcon sx={{ color: theme.palette.info.main }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Overtime Hours
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {laborMetrics.overtimeHours}
                  </Typography>
                  <Typography variant="caption" color="warning.main">
                    Above target by 8h
                  </Typography>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={2.4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.4 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(76, 175, 80, 0.2)' }}>
                  <TrendingUpIcon sx={{ color: theme.palette.success.main }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Productivity
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {laborMetrics.avgProductivity}%
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    +5% from target
                  </Typography>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={2.4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.5 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(156, 39, 176, 0.2)' }}>
                  <StarIcon sx={{ color: '#9c27b0' }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Avg Rating
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    4.7/5.0
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    Above benchmark
                  </Typography>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Staff Performance" />
          <Tab label="Scheduling" />
          <Tab label="Labor Costs" />
          <Tab label="Training & Development" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Top Performers Table */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Staff Performance Metrics
              </Typography>
              
              <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Efficiency</TableCell>
                      <TableCell>Sales/Hour</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Hours</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employees.map((employee) => (
                      <TableRow key={employee.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {employee.name.split(' ').map(n => n[0]).join('')}
                            </Avatar>
                            <Typography variant="body2">{employee.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={employee.role} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={employee.status}
                            size="small"
                            sx={{
                              backgroundColor: `${getStatusColor(employee.status)}20`,
                              color: getStatusColor(employee.status),
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={employee.efficiency}
                              sx={{
                                width: 60,
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 3,
                                  backgroundColor: employee.efficiency >= 90 ? theme.palette.success.main : theme.palette.warning.main,
                                },
                              }}
                            />
                            <Typography variant="body2">{employee.efficiency}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>${employee.salesPerHour}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon sx={{ fontSize: 16, color: getRatingColor(employee.customerRating) }} />
                            <Typography variant="body2" sx={{ color: getRatingColor(employee.customerRating) }}>
                              {employee.customerRating}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{employee.hoursWorked}h</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* Performance Radar */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ ...glassStyle, p: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Team Performance Comparison
              </Typography>
              
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={performanceMetrics}>
                  <PolarGrid stroke="rgba(255, 255, 255, 0.1)" />
                  <PolarAngleAxis dataKey="metric" stroke="#A0AEC0" />
                  <PolarRadiusAxis stroke="#A0AEC0" domain={[0, 100]} />
                  <Radar name="Current Week" dataKey="Current Week" stroke="#af2326" fill="#af2326" fillOpacity={0.3} />
                  <Radar name="Last Week" dataKey="Last Week" stroke="#1976d2" fill="#1976d2" fillOpacity={0.3} />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {/* Weekly Schedule Variance */}
          <Grid item xs={12}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Weekly Schedule Analysis
              </Typography>
              
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={weeklySchedule}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis dataKey="day" stroke="#A0AEC0" />
                  <YAxis stroke="#A0AEC0" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="scheduled" fill="#1976d2" name="Scheduled Hours" />
                  <Bar dataKey="actual" fill="#af2326" name="Actual Hours" />
                </BarChart>
              </ResponsiveContainer>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Schedule Adherence Insights
                </Typography>
                <Grid container spacing={2}>
                  {weeklySchedule.map((day) => (
                    <Grid item xs={12} sm={6} md={3} key={day.day}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {day.variance === 0 ? (
                          <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                        ) : (
                          <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                        )}
                        <Typography variant="body2">
                          {day.day}: {day.variance > 0 ? '+' : ''}{day.variance}h variance
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {/* Labor Cost by Role */}
          <Grid item xs={12} md={6}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Labor Cost Distribution
              </Typography>
              
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={laborByRole}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="cost"
                  >
                    {laborByRole.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                </PieChart>
              </ResponsiveContainer>
              
              <Box sx={{ mt: 2 }}>
                {laborByRole.map((role) => (
                  <Box key={role.role} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: role.color }} />
                      <Typography variant="body2">{role.role}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ${role.cost.toLocaleString()} ({role.hours}h)
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>

          {/* Labor Cost Trends */}
          <Grid item xs={12} md={6}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Labor Cost vs Sales Trend
              </Typography>
              
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={[
                    { week: 'W1', laborCost: 11200, sales: 42000, percentage: 26.7 },
                    { week: 'W2', laborCost: 11800, sales: 44500, percentage: 26.5 },
                    { week: 'W3', laborCost: 12100, sales: 43800, percentage: 27.6 },
                    { week: 'W4', laborCost: 12450, sales: 43700, percentage: 28.5 },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis dataKey="week" stroke="#A0AEC0" />
                  <YAxis yAxisId="left" stroke="#A0AEC0" />
                  <YAxis yAxisId="right" orientation="right" stroke="#A0AEC0" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="laborCost" stroke="#af2326" name="Labor Cost ($)" />
                  <Line yAxisId="left" type="monotone" dataKey="sales" stroke="#1976d2" name="Sales ($)" />
                  <Line yAxisId="right" type="monotone" dataKey="percentage" stroke="#ff6b35" name="Labor %" />
                </LineChart>
              </ResponsiveContainer>
              
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon sx={{ fontSize: 20, color: theme.palette.warning.main }} />
                  Labor cost percentage is trending above the 27% target
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          {/* Training Progress */}
          <Grid item xs={12}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Training & Certification Status
              </Typography>
              
              <Grid container spacing={2}>
                {[
                  { name: 'Food Safety Certification', completed: 42, total: 45, dueDate: '2024-03-15' },
                  { name: 'Customer Service Excellence', completed: 38, total: 45, dueDate: '2024-04-01' },
                  { name: 'POS System Training', completed: 45, total: 45, dueDate: 'Completed' },
                  { name: 'Management Development', completed: 8, total: 12, dueDate: '2024-05-01' },
                ].map((training) => (
                  <Grid item xs={12} md={6} key={training.name}>
                    <Box sx={{ p: 2, border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {training.name}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Progress: {training.completed}/{training.total} employees
                        </Typography>
                        <Typography variant="body2" color={training.dueDate === 'Completed' ? 'success.main' : 'text.secondary'}>
                          {training.dueDate}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(training.completed / training.total) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: training.completed === training.total ? theme.palette.success.main : theme.palette.primary.main,
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <Button variant="contained" color="primary">
                  Schedule Training Sessions
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default LaborAnalytics;